package com.kinoshazam.screens.search.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.search.components.IdentifyWandButton
import com.kinoshazam.screens.search.components.SearchModeSwitcher
import com.kinoshazam.uiKit.components.textField.TextFieldState
import com.kinoshazam.uiKit.components.textField.compact.CompactTextField
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.CompactVideoItem
import com.kinoshazam.uiKit.components.videoItem.compactBannerSkeletons
import com.kinoshazam.uiKit.components.videoItem.MovieCastItem
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.ErrorView
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.identify_empty
import kinoshazam.resources.generated.resources.identify_hint
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.loading
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.repeat
import kinoshazam.resources.generated.resources.search
import kinoshazam.resources.generated.resources.search_actors
import kinoshazam.resources.generated.resources.search_empty
import kinoshazam.resources.generated.resources.search_result
import kinoshazam.resources.generated.resources.search_videos
import models.Actor
import models.PersonPreview
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.search.SearchMVIAction
import presentation.screens.search.SearchState

@Composable
fun CompactSearchContent(
    state: SearchState,
    onAction: (SearchMVIAction) -> Unit,
) {

    val searchFocus = remember { FocusRequester() }
    val identifyFocus = remember { FocusRequester() }
    LaunchedEffect(state.identifyMode) {
        delay(420)
        if (state.identifyMode) {
            runCatching { identifyFocus.requestFocus() }
        } else {
            runCatching { searchFocus.requestFocus() }
        }
    }

    Scaffold(
        topBar = {
            Row(
                modifier = Modifier
                    .statusBarsPadding()
                    .fillMaxWidth()
                    .padding(start = 4.dp, end = 8.dp, top = 8.dp, bottom = 8.dp),
                verticalAlignment = Alignment.Top,
            ) {
                Image(
                    painter = painterResource(Res.drawable.left),
                    colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                    contentDescription = null,
                    modifier = Modifier
                        .padding(top = 16.dp, start = 8.dp, end = 4.dp)
                        .size(24.dp)
                        .clickable { onAction(SearchMVIAction.BackClicked) },
                )
                SearchModeSwitcher(
                    identifyMode = state.identifyMode,
                    identifyEnabled = state.identifyEnabled,
                    modifier = Modifier
                        .weight(1f)
                        .padding(top = 8.dp),
                    titleContent = {
                        CompactTextField(
                            modifier = Modifier.fillMaxWidth(),
                            state = TextFieldState(
                                textValue = state.query,
                                hasClearButton = true,
                                isSingleLine = true,
                                imeAction = ImeAction.Search,
                                placeholder = TextLabelState(
                                    text = stringResource(Res.string.search),
                                    color = UiKitTheme.colors.color_2,
                                    maxLines = 1,
                                ),
                            ),
                            focusRequester = searchFocus,
                            keyboardActions = KeyboardActions(
                                onSearch = { onAction(SearchMVIAction.SubmitSearch) },
                            ),
                            onValueChanged = { query ->
                                onAction(SearchMVIAction.OnTextChanged(query))
                            },
                            onClearIconClick = {
                                onAction(SearchMVIAction.OnTextChanged(""))
                            },
                        )
                    },
                    identifyContent = {
                        CompactTextField(
                            modifier = Modifier.fillMaxWidth(),
                            state = TextFieldState(
                                textValue = state.identifyQuery,
                                hasClearButton = true,
                                isSingleLine = false,
                                minLines = 1,
                                maxLines = 6,
                                hasProgress = state.isLoading && state.identifyMode,
                                imeAction = ImeAction.Search,
                                containerColor = UiKitTheme.colors.color_3,
                                placeholder = TextLabelState(
                                    text = stringResource(Res.string.identify_hint),
                                    color = UiKitTheme.colors.color_2,
                                    maxLines = 2,
                                ),
                            ),
                            focusRequester = identifyFocus,
                            keyboardActions = KeyboardActions(
                                onSearch = { onAction(SearchMVIAction.SubmitSearch) },
                            ),
                            onValueChanged = { query ->
                                onAction(SearchMVIAction.OnIdentifyTextChanged(query))
                            },
                            onClearIconClick = {
                                onAction(SearchMVIAction.OnIdentifyTextChanged(""))
                            },
                        )
                    },
                    wand = {
                        IdentifyWandButton(
                            selected = state.identifyMode,
                            onClick = { onAction(SearchMVIAction.ToggleIdentifyMode) },
                        )
                    },
                )
            }
        },
        containerColor = UiKitTheme.colors.color_3,
        content = { paddingValues ->
            when {
                state.isLoading -> LoadingScreen()
                state.isError -> ErrorScreen(onAction)
                else -> ContentScreen(paddingValues, state, onAction)
            }
        }
    )
}

@Composable
private fun ErrorScreen(
    onAction: (SearchMVIAction) -> Unit,
) {
    ErrorView(
        textError = stringResource(Res.string.error),
        textAction = stringResource(Res.string.repeat),
        onAction = { onAction(SearchMVIAction.Reload) }
    )
}

@Composable
private fun LoadingScreen() {
    LazyVerticalGrid(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 48.dp),
        contentPadding = PaddingValues(top = 16.dp, bottom = 32.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        columns = GridCells.Adaptive(128.dp),
    ) {
        compactBannerSkeletons()
    }
}

@Composable
private fun StatusMessage(text: String) {
    Box(modifier = Modifier.fillMaxSize()) {
        Text(
            text = text,
            color = UiKitTheme.colors.color_2,
            modifier = Modifier.align(Alignment.Center),
        )
    }
}

@Composable
private fun ContentScreen(
    paddingValues: PaddingValues,
    state: SearchState,
    onAction: (SearchMVIAction) -> Unit,
) {
    when {
        state.videos == null -> StatusMessage(stringResource(Res.string.search_result))
        !state.hasResults -> StatusMessage(
            stringResource(
                if (state.identifyMode) Res.string.identify_empty else Res.string.search_empty
            )
        )
        else -> {
            val persons = state.persons.orEmpty()
            val videos = state.videos.orEmpty()
            val scrollState = rememberLazyGridState()
            val fetchNextPage: Boolean by remember {
                derivedStateOf {
                    val currentVideoCount = scrollState.layoutInfo.totalItemsCount - 1
                    val lastVisibleIndex =
                        scrollState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                            ?: return@derivedStateOf false
                    return@derivedStateOf lastVisibleIndex >= currentVideoCount - 5
                }
            }

            LaunchedEffect(key1 = fetchNextPage, key2 = state.identifyMode) {
                if (fetchNextPage && !state.identifyMode) {
                    onAction(SearchMVIAction.FetchNextPage)
                }
            }

            LazyVerticalGrid(
                state = scrollState,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .padding(horizontal = 48.dp),
                contentPadding = PaddingValues(
                    top = 16.dp,
                    bottom = 32.dp,
                ),
                verticalArrangement = Arrangement.spacedBy(4.dp),
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                columns = GridCells.Adaptive(128.dp)
            ) {
                if (persons.isNotEmpty() && !state.identifyMode) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        SearchSectionTitle(stringResource(Res.string.search_actors))
                    }
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        LazyRow(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            contentPadding = PaddingValues(bottom = 16.dp),
                        ) {
                            items(persons, key = { it.id }) { person ->
                                MovieCastItem(
                                    actor = person.toActor(),
                                    onClick = {
                                        onAction(SearchMVIAction.OnPersonClicked(person.id))
                                    },
                                )
                            }
                        }
                    }
                }
                if (videos.isNotEmpty()) {
                    if (persons.isNotEmpty() && !state.identifyMode) {
                        item(span = { GridItemSpan(maxLineSpan) }) {
                            SearchSectionTitle(stringResource(Res.string.search_videos))
                        }
                    }
                    itemsIndexed(videos) { _, video ->
                        CompactVideoItem(
                            state = VideoState(
                                id = video.id ?: -1,
                                title = video.title.orEmpty(),
                                image = video.imageUrl,
                                placeHolder = painterResource(Res.drawable.movie),
                                placeHolderColor = UiKitTheme.colors.color_5,
                                onAction = { id ->
                                    onAction(SearchMVIAction.OnVideoClicked(id, video.isTvShow))
                                }
                            ),
                        )
                    }
                }
                if (state.isNextLoading && !state.identifyMode) {
                    item {
                        Text(
                            text = "Load next page",
                            color = UiKitTheme.colors.color_2,
                        )
                    }
                }
            }
        }

    }
}

@Composable
private fun SearchSectionTitle(text: String) {
    TextLabel(
        state = TextLabelState(
            text = text,
            color = UiKitTheme.colors.color_2,
            textStyle = BoldLarge,
        ),
        modifier = Modifier.padding(top = 8.dp, bottom = 12.dp),
    )
}

private fun PersonPreview.toActor() = Actor(
    castId = null,
    character = null,
    creditId = null,
    id = id,
    name = name,
    originalName = null,
    profilePath = profilePath,
)